import { useState, useCallback } from "react";
import { iamApi } from "@/lib/api";
import type { IamMemberApi } from "@/lib/api";
import { useIamOrganizationMembers, useIamRoles } from "@/hooks";

const ROLE_LABELS: Record<string, string> = {
  TECHNICIAN_FIELD: "Tecnico de Campo",
  SUPERVISOR_FIELD: "Supervisor de Campo",
  ARBORICULTURE_ANALYST: "Analista de Arboricultura",
  AUDITOR: "Auditor",
  ORG_ADMIN: "Administrador",
};

const PLATFORM_LABELS: Record<string, string> = {
  FLORAIQ_WEB: "Web",
  FLORAIQ_APP: "App",
  FLORAIQ_ADMIN: "Admin",
  FLORAIQ_AUDIT: "Auditoria",
};

function StatusBadge({ status }: { status: string }) {
  const isActive = status === "ACTIVE";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
        isActive
          ? "bg-accent-100 text-accent-700"
          : "bg-foreground-100 text-foreground-500"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-accent-500" : "bg-foreground-400"}`} />
      {isActive ? "Ativo" : "Suspenso"}
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-primary-50 text-primary-700">
      {ROLE_LABELS[role] ?? role}
    </span>
  );
}

function PlatformBadges({ platforms }: { platforms: string[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {(platforms ?? []).map((p) => (
        <span key={p} className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-background-100 text-foreground-600">
          {PLATFORM_LABELS[p] ?? p}
        </span>
      ))}
    </div>
  );
}

// --- Create User Modal ---
function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role_code: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError(null);
    if (!form.name || !form.email || !form.password || !form.role_code) {
      setError("Preencha todos os campos obrigatorios.");
      return;
    }
    setSubmitting(true);
    try {
      await iamApi.createUser({
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        password: form.password,
        role_code: form.role_code,
      });
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("409")) setError("Ja existe um usuario com este email.");
      else if (msg.includes("422")) setError("Dados invalidos. Verifique o perfil e email.");
      else setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-background-200/70 shadow-lg w-full max-w-lg">
        <div className="border-b border-background-200/70 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h3 className="text-base font-semibold text-foreground-900 font-heading">Novo Usuario</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 text-sm bg-danger-50 text-danger-700 rounded-lg border border-danger-200">{error}</div>
          )}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Nome completo *</label>
            <input type="text" value={form.name} onChange={(e) => handleChange("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" placeholder="Nome do usuario" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">E-mail *</label>
            <input type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" placeholder="usuario@exemplo.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Perfil de acesso *</label>
            <select value={form.role_code} onChange={(e) => handleChange("role_code", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
              <option value="">Selecione um perfil...</option>
              {Object.entries(ROLE_LABELS).map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Senha inicial *</label>
            <input type="password" value={form.password} onChange={(e) => handleChange("password", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" placeholder="Senha de acesso" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Telefone</label>
            <input type="text" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" placeholder="(62) 90000-0000" />
          </div>
        </div>
        <div className="border-t border-background-200/70 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors">Cancelar</button>
          <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50">
            {submitting ? "Criando..." : "Criar usuario"}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Edit User Modal ---
function EditUserModal({
  member,
  onClose,
  onSaved,
}: {
  member: IamMemberApi;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(member.user_name);
  const [roleCode, setRoleCode] = useState(member.roles?.[0] ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const originalRole = member.roles?.[0] ?? "";

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (name !== member.user_name) {
        await iamApi.updateUser(member.user_id, { name });
      }
      if (roleCode && roleCode !== originalRole) {
        await iamApi.changeUserRole(member.user_id, { role_code: roleCode });
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-background-200/70 shadow-lg w-full max-w-lg">
        <div className="border-b border-background-200/70 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h3 className="text-base font-semibold text-foreground-900 font-heading">Editar Usuario</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 text-sm bg-danger-50 text-danger-700 rounded-lg border border-danger-200">{error}</div>
          )}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Nome</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">E-mail</label>
            <input type="email" value={member.user_email} disabled className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-500 cursor-not-allowed" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Perfil de acesso</label>
            <select value={roleCode} onChange={(e) => setRoleCode(e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
              {Object.entries(ROLE_LABELS).map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
            {roleCode !== originalRole && (
              <p className="mt-1 text-xs text-warning-600">Alterar o perfil ira revogar o acesso anterior e configurar novas plataformas.</p>
            )}
          </div>
        </div>
        <div className="border-t border-background-200/70 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors">Cancelar</button>
          <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50">
            {submitting ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Panel ---
export default function UserManagementPanel() {
  const orgId = 10; // demo org — from JWT context in production
  const { members, loading, error } = useIamOrganizationMembers(orgId);
  const { roles } = useIamRoles();
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editMember, setEditMember] = useState<IamMemberApi | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // re-fetch on refreshKey change — the hook depends on orgId, so we force it
  // by using a key pattern: the hook is called with the same orgId but the component re-renders
  void refreshKey;

  const filtered = members.filter((m) => {
    if (search) {
      const q = search.toLowerCase();
      if (!m.user_name.toLowerCase().includes(q) && !m.user_email.toLowerCase().includes(q)) return false;
    }
    if (filterRole && !(m.roles ?? []).includes(filterRole)) return false;
    if (filterStatus && m.status !== filterStatus) return false;
    return true;
  });

  const handleToggleStatus = async (member: IamMemberApi) => {
    setActionLoading(member.user_id);
    try {
      if (member.status === "ACTIVE") {
        await iamApi.deactivateUser(member.user_id);
      } else {
        await iamApi.reactivateUser(member.user_id);
      }
      refresh();
      window.location.reload();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Erro ao alterar status");
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-foreground-400">
        <i className="ri-loader-4-line animate-spin text-xl mr-2" /> Carregando usuarios...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-danger-50 text-danger-700 rounded-lg border border-danger-200 text-sm">
        Erro ao carregar usuarios: {error}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Users section */}
      <div>
        <h3 className="text-sm font-semibold text-foreground-900 mb-1">Usuarios da organizacao</h3>
        <p className="text-xs text-foreground-500 mb-4">
          Gerencie os usuarios com acesso a plataforma ({members.length} usuario{members.length !== 1 ? "s" : ""})
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none"
        >
          <option value="">Todos os perfis</option>
          {(roles ?? []).map((r) => (
            <option key={r.code} value={r.code}>{ROLE_LABELS[r.code] ?? r.code}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none"
        >
          <option value="">Todos os status</option>
          <option value="ACTIVE">Ativo</option>
          <option value="SUSPENDED">Suspenso</option>
        </select>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-user-add-line w-4 h-4 inline-flex items-center justify-center mr-1" />
          Novo usuario
        </button>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-foreground-400 text-sm">
          {members.length === 0 ? "Nenhum usuario cadastrado." : "Nenhum usuario encontrado com os filtros aplicados."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Nome</th>
                <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">E-mail</th>
                <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Perfil</th>
                <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Plataformas</th>
                <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Status</th>
                <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.user_id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                  <td className="py-2.5 px-3 text-foreground-900 font-medium">{m.user_name}</td>
                  <td className="py-2.5 px-3 text-foreground-500 text-xs">{m.user_email}</td>
                  <td className="py-2.5 px-3">
                    <div className="flex flex-wrap gap-1">
                      {(m.roles ?? []).map((r) => <RoleBadge key={r} role={r} />)}
                    </div>
                  </td>
                  <td className="py-2.5 px-3"><PlatformBadges platforms={m.platforms ?? []} /></td>
                  <td className="py-2.5 px-3"><StatusBadge status={m.status} /></td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditMember(m)}
                        className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-primary-600 transition-colors"
                        title="Editar"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(m)}
                        disabled={actionLoading === m.user_id}
                        className={`w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center transition-colors ${
                          m.status === "ACTIVE"
                            ? "text-foreground-400 hover:text-danger-600"
                            : "text-foreground-400 hover:text-accent-600"
                        }`}
                        title={m.status === "ACTIVE" ? "Desativar" : "Reativar"}
                      >
                        {actionLoading === m.user_id ? (
                          <i className="ri-loader-4-line animate-spin text-sm" />
                        ) : m.status === "ACTIVE" ? (
                          <i className="ri-lock-line text-sm" />
                        ) : (
                          <i className="ri-lock-unlock-line text-sm" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Roles summary */}
      <div className="border-t border-background-200 pt-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1">Perfis do sistema</h3>
        <p className="text-xs text-foreground-500 mb-4">Perfis de acesso predefinidos e suas plataformas automaticas</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(ROLE_LABELS).map(([code, label]) => {
            const count = members.filter((m) => (m.roles ?? []).includes(code)).length;
            return (
              <div key={code} className="p-3 border border-background-200 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground-900">{label}</span>
                  <span className="text-xs text-foreground-500">{count} usuario{count !== 1 ? "s" : ""}</span>
                </div>
                <div className="text-xs text-foreground-500">{code}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { refresh(); window.location.reload(); }}
        />
      )}
      {editMember && (
        <EditUserModal
          member={editMember}
          onClose={() => setEditMember(null)}
          onSaved={() => { refresh(); window.location.reload(); }}
        />
      )}
    </div>
  );
}
